import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '../hooks/useAuthContext';
import { getFirestore, doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { updateProfile } from 'firebase/auth';
import { toast } from 'react-hot-toast';
import { parseDisplayName } from '../utils/nameUtils';
import { createUserProfile } from '../services/firebase/user-profile';

// Step Components
import LinkedInStep from '../components/features/onboarding/steps/LinkedInStep';
import CompanyRoleStep from '../components/features/onboarding/steps/CompanyRoleStep';
import ProfilePictureStep from '../components/features/onboarding/steps/ProfilePictureStep';
import LocationStep from '../components/features/onboarding/steps/LocationStep';
import JobStatusStep from '../components/features/onboarding/steps/JobStatusStep';

const OnboardingFlow = () => {
  const totalSteps = 5;
  const { user, userProfile, fetchUserProfile } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();

  const getStepFromHash = () => {
    const hash = location.hash;
    if (hash) {
      const step = parseInt(hash.replace('#step-', ''), 10);
      if (!isNaN(step) && step >= 1 && step <= totalSteps) {
        return step;
      }
    }
    return 1;
  };

  // Initialize state from sessionStorage or with default values
  const [currentStep, setCurrentStep] = useState(getStepFromHash);

  const [onboardingData, setOnboardingData] = useState(() => {
    try {
      const savedData = sessionStorage.getItem('onboardingData');
      if (savedData) {
        return JSON.parse(savedData);
      }
    } catch (error) {
      console.warn('Failed to read onboarding data from sessionStorage:', error);
    }
    return {
      linkedinUrl: '',
      currentRole: '',
      companyId: '',
      companyName: '',
      profilePicture: null,
      location: '',
      locationData: null, // Structured location data from Google Places
      jobSearchStatus: ''
    };
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [stepDirection, setStepDirection] = useState('forward'); // 'forward' or 'backward'

  // Persist state to sessionStorage on change
  useEffect(() => {
    try {
      sessionStorage.setItem('onboardingData', JSON.stringify(onboardingData));
    } catch (error) {
      console.warn('Could not save onboarding state to sessionStorage:', error);
    }
  }, [onboardingData]);

  // Sync step with URL hash
  useEffect(() => {
    const targetHash = `#step-${currentStep}`;
    if (location.hash !== targetHash) {
      navigate(targetHash, { replace: true });
    }
  }, [currentStep, navigate, location.hash]);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handleHashChange = () => {
      const newStep = getStepFromHash();
      if(newStep !== currentStep) {
        setStepDirection(newStep > currentStep ? 'forward' : 'backward');
        setIsTransitioning(true);
        setTimeout(() => {
          setCurrentStep(newStep);
          setIsTransitioning(false);
        }, 150);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [currentStep]);

  // Check if user has already completed onboarding
  useEffect(() => {
    if (user && userProfile && userProfile.onboardingComplete) {
      // Onboarding is done, so we can clear the session storage
      try {
        sessionStorage.removeItem('onboardingData');
      } catch (error) {
        console.warn('Could not remove onboarding state from sessionStorage:', error);
      }
      navigate('/network');
    }
  }, [user, userProfile, navigate]);

  // Prepopulate LinkedIn URL from user profile if available
  useEffect(() => {
    if (userProfile?.basicInfo?.linkedinUrl && !onboardingData.linkedinUrl) {
      setOnboardingData(prev => ({
        ...prev,
        linkedinUrl: userProfile.basicInfo.linkedinUrl
      }));
    }
  }, [userProfile]);

  const progress = (currentStep / totalSteps) * 100;

  // Handle next step with animation
  const handleNext = () => {
    if (currentStep < totalSteps) {
      setStepDirection('forward');
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentStep(currentStep + 1);
        setIsTransitioning(false);
      }, 150);
    } else {
      handleSubmit();
    }
  };

  // Handle previous step with animation
  const handlePrevious = () => {
    if (currentStep > 1) {
      setStepDirection('backward');
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentStep(currentStep - 1);
        setIsTransitioning(false);
      }, 150);
    }
  };

  // Update onboarding data
  const updateData = (field, value) => {
    setOnboardingData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Validate current step
  const validateStep = () => {
    switch (currentStep) {
      case 1:
        return onboardingData.linkedinUrl.trim() !== '';
      case 2:
        return onboardingData.currentRole.trim() !== '' && onboardingData.companyName.trim() !== '';
      case 3:
        return true; // Profile picture is optional
      case 4:
        return onboardingData.location.trim() !== '';
      case 5:
        return onboardingData.jobSearchStatus !== '';
      default:
        return false;
    }
  };

  // Submit final onboarding data
  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      const db = getFirestore();
      
      // Parse firstName and lastName from user's displayName
      const { firstName, lastName } = parseDisplayName(user.displayName);
      
      // Prepare profile data
      const profileData = {
        basicInfo: {
          firstName: firstName,
          lastName: lastName,
          linkedinUrl: onboardingData.linkedinUrl,
          currentRole: onboardingData.currentRole,
          companyId: onboardingData.companyId || null,
          companyName: onboardingData.companyName || null,
          location: onboardingData.location,
          locationData: onboardingData.locationData || null, // Structured location data
          email: user.email,
          name: user.displayName || `${firstName} ${lastName}`.trim()
        },
        preferences: {
          jobSearchStatus: onboardingData.jobSearchStatus
        },
        onboardingComplete: true,
        lastUpdated: serverTimestamp()
      };

      // Add profile picture if uploaded
      if (onboardingData.profilePicture) {
        profileData.profilePicture = onboardingData.profilePicture;
        profileData.basicInfo.photoURL = onboardingData.profilePicture;
        profileData.photoURL = onboardingData.profilePicture; // Keep for backward compatibility
      }

      // Note: Status is now automatically set to "active" at login time in AuthContext
      // for users with "invited" status or no status (legacy users)
      // No need to update status here during onboarding

      // Ensure user profile document exists before updating
      const profileRef = doc(db, 'user_profiles', user.uid);
      const profileSnap = await getDoc(profileRef);
      
      if (!profileSnap.exists()) {
        // Create basic user profile first if it doesn't exist
        console.log('User profile not found, creating new profile...');
        const createResult = await createUserProfile(user.uid);
        if (!createResult.success) {
          throw new Error(`Failed to create user profile: ${createResult.error}`);
        }
        console.log('User profile created successfully');
      }

      // Now update the profile with onboarding data using setDoc with merge
      await setDoc(profileRef, profileData, { merge: true });

      // Update Firebase Auth profile (only photoURL if provided, displayName should already be set from signup)
      if (onboardingData.profilePicture) {
        await updateProfile(user, {
          photoURL: onboardingData.profilePicture
        });
      }

      // Set flag to trigger walkthrough BEFORE refreshing profile
      // This ensures the flag is in place when the navigation happens
      try {
        sessionStorage.setItem('justCompletedOnboarding', 'true');
      } catch (error) {
        console.warn('Could not set walkthrough trigger:', error);
      }

      // Clean up sessionStorage
      try {
        sessionStorage.removeItem('onboardingData');
      } catch (error) {
        console.warn('Could not remove onboarding state from sessionStorage:', error);
      }

      // Refresh the user profile in AuthContext to update onboardingComplete status
      await fetchUserProfile(user);

      toast.success('Profile setup complete!');
      navigate('/network');
      
    } catch (error) {
      console.error('Error completing onboarding:', error);
      toast.error('Failed to complete setup. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render current step component
  const renderStep = () => {
    const stepProps = {
      data: onboardingData,
      updateData,
      onNext: handleNext,
      onPrevious: handlePrevious,
      isValid: validateStep(),
      isFirstStep: currentStep === 1,
      isLastStep: currentStep === totalSteps,
      isSubmitting
    };

    switch (currentStep) {
      case 1:
        return <LinkedInStep {...stepProps} />;
      case 2:
        return <CompanyRoleStep {...stepProps} />;
      case 3:
        return <ProfilePictureStep {...stepProps} />;
      case 4:
        return <LocationStep {...stepProps} />;
      case 5:
        return <JobStatusStep {...stepProps} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex flex-col">
      {/* Enhanced Progress Bar */}
      <div className="w-full bg-white/20 dark:bg-gray-800/50 h-1 relative overflow-hidden">
        <div 
          className="bg-gradient-to-r from-purple-600 to-indigo-600 h-1 transition-all duration-700 ease-out shadow-sm relative"
          style={{ width: `${progress}%` }}
        >
          {/* Animated shimmer effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></div>
        </div>
      </div>
      
      {/* Enhanced Step Counter */}
      <div className="px-6 py-6">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center space-x-3">
            <p className="text-sm font-medium text-purple-600 dark:text-purple-400 tracking-wide uppercase transition-all duration-300">
              Step {currentStep} of {totalSteps}
            </p>
            {/* Step dots indicator */}
            <div className="flex space-x-1">
              {Array.from({ length: totalSteps }, (_, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index + 1 <= currentStep
                      ? 'bg-purple-600 dark:bg-purple-400 scale-110'
                      : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content with Step Transitions */}
      <div className="flex-1 flex items-center justify-center px-6 py-8">
        <div className="w-full max-w-2xl">
          <div 
            className={`transition-all duration-300 ease-in-out transform ${
              isTransitioning 
                ? stepDirection === 'forward' 
                  ? 'opacity-0 translate-x-8 scale-95' 
                  : 'opacity-0 -translate-x-8 scale-95'
                : 'opacity-100 translate-x-0 scale-100'
            }`}
          >
            {renderStep()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingFlow; 